import { useCallback, useState } from 'react';
import {
  dealScenarioDetailSchema,
  dealScenarioSchema,
  type DealScenario,
  type DealScenarioDetail,
  type ScenarioInput,
  type ScenarioKind,
  type ScenarioPeriodUnit,
  type ScenarioStatus,
} from '@forecastify/shared';
import type { BadgeVariant } from '@forecastify/ui';
import { useT, type Locale } from '../../i18n';
import { useLiveInvalidation } from '../../live/use-live';
import { DEAL_TOPICS } from '../../live/topics';
import { useSession } from '../../session';
import { failureOf, type ScreenFailure } from '../screen-state';
import { useFirstLoad } from '../use-first-load';
import { dealRefusal } from './deal-kit';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 20 Oct 2026 · Phase 6 · D9 · what the scenario screens share. DS-1…DS-6.  │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The request helper, the two loaders (the list, one scenario), the input the shared arithmetic
 * takes, and the labels. ⚠️ **Every figure on these screens comes from `scenarioFigures` /
 * `scenarioPeriods`** — the server's own functions — fed by `scenarioInput` below, so the list's
 * columns and the editor's tiles cannot disagree with what the server would say.
 */

type T = ReturnType<typeof useT>;

/** `/deals/<uuid>/scenarios<rest>`. */
export const scenarioPath = (dealId: string, rest = ''): string =>
  `/deals/${encodeURIComponent(dealId)}/scenarios${rest}`;

/** The shared arithmetic's input, from a scenario as the server sent it. */
export function scenarioInput(detail: DealScenarioDetail, withOptions: boolean): ScenarioInput {
  return {
    kind: detail.kind,
    startsOn: detail.startsOn,
    periodUnit: detail.periodUnit,
    periodStep: detail.periodStep,
    periodCount: detail.periodCount,
    indexPct: Number(detail.indexPct),
    lines: detail.lines.map((line) => ({
      id: line.id,
      type: line.type,
      amount: Number(line.amount),
      startsOn: line.startsOn,
      endsOn: line.endsOn,
    })),
    resources: detail.resources.map((resource) => ({
      id: resource.id,
      headcount: resource.headcount,
      costRate: Number(resource.costRate),
    })),
    cells: detail.cells.map((cell) => ({
      lineId: cell.lineId,
      resourceId: cell.resourceId,
      period: cell.period,
      hours: Number(cell.hours),
    })),
    costs: detail.costs.map((cost) => ({ amount: Number(cost.amount) })),
    overrides: Object.fromEntries(
      Object.entries(detail.overrides).map(([index, value]) => [index, Number(value)]),
    ),
    withOptions,
  };
}

/** Months in one period — the FTE tile's divisor on a recurring scenario. */
export function monthsPerPeriod(unit: ScenarioPeriodUnit, step: number): number {
  if (unit === 'year') return 12;
  if (unit === 'quarter') return 3;
  return Math.max(1, step);
}

/* ── labels ──────────────────────────────────────────────────────────────────────────────── */

export function scenarioStatusLabel(t: T, status: ScenarioStatus): string {
  if (status === 'sent') return t('dealScenario.status.sent');
  if (status === 'won') return t('dealScenario.status.won');
  if (status === 'dropped') return t('dealScenario.status.dropped');
  return t('dealScenario.status.draft');
}

/** The mockup's `STATUS` table — a won scenario is the one filled badge. */
export function scenarioStatusVariant(status: ScenarioStatus): BadgeVariant {
  if (status === 'won') return 'default';
  if (status === 'sent') return 'secondary';
  return 'outline';
}

export function scenarioKindLabel(t: T, kind: ScenarioKind): string {
  return kind === 'recurring' ? t('dealScenario.kind.recurring') : t('dealScenario.kind.one_off');
}

export function periodUnitLabel(t: T, unit: ScenarioPeriodUnit): string {
  if (unit === 'quarter') return t('dealScenario.unit.quarter');
  if (unit === 'year') return t('dealScenario.unit.year');
  return t('dealScenario.unit.month');
}

/** `2027. jan` / `Jan 2027` — a month as the mockup labels it. */
export function monthLabel(wire: string, locale: Locale): string {
  const [y = 1970, m = 1] = wire.split('-').map(Number);
  const text = new Date(y, m - 1, 1).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
  });
  return text.replace(/\.$/u, '');
}

/** `nov` over `2026` — a grid column's two lines. */
export function monthParts(wire: string, locale: Locale): { top: string; year: string } {
  const [y = 1970, m = 1] = wire.split('-').map(Number);
  const top = new Date(y, m - 1, 1)
    .toLocaleDateString(locale, { month: 'short' })
    .replace(/\.$/u, '');
  return { top, year: String(y) };
}

/** `2026. nov → 2027. ápr`, or a dash when nothing is planned. */
export function spanText(from: string | null, to: string | null, locale: Locale): string {
  if (from === null || to === null) return '—';
  return `${monthLabel(from, locale)} → ${monthLabel(to, locale)}`;
}

/** `48,7%` — a ratio, or the dash that means uncomputable (`state-rules.md` §5). */
export function pctText(ratio: number | null, locale: Locale): string {
  if (ratio === null || !Number.isFinite(ratio)) return '—';
  return new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 1 }).format(
    ratio,
  );
}

/** `+3%` / `−2%` — a signed change. */
export function signedPct(ratio: number, locale: Locale): string {
  const text = new Intl.NumberFormat(locale, {
    style: 'percent',
    maximumFractionDigits: 1,
    signDisplay: 'exceptZero',
  }).format(ratio);
  return text;
}

/** A typed amount as an input shows it: `1 800 000`. */
export function amountField(value: number, locale: Locale): string {
  if (value === 0) return '0';
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(value);
}

/* ── requests ────────────────────────────────────────────────────────────────────────────── */

/**
 * A scenario request. Resolves to the response, or the refusal's sentence — a refused write is
 * shown beside what was being changed, never as a screen failure.
 */
export function useScenarioCall(
  dealId: string,
): (rest: string, method?: string, body?: unknown) => Promise<Response | string> {
  const session = useSession();
  const t = useT();
  return useCallback(
    async (rest: string, method = 'GET', body?: unknown): Promise<Response | string> => {
      const response = await session
        .request(scenarioPath(dealId, rest), {
          method,
          ...(body === undefined ? {} : { body: JSON.stringify(body) }),
        })
        .catch(() => null);
      if (response === null) return t('error.unavailable');
      if (!response.ok) return dealRefusal(response, t);
      return response;
    },
    [session, dealId, t],
  );
}

/** The deal's scenarios. Reloads on the deal topics — every scenario write is a `deal.*` action. */
export function useScenarioList(dealId: string) {
  const session = useSession();
  const [scenarios, setScenarios] = useState<readonly DealScenario[]>([]);
  const [failure, setFailure] = useState<ScreenFailure | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    const response = await session.request(scenarioPath(dealId)).catch(() => null);
    if (response === null) {
      setFailure({ kind: 'error', code: 'unavailable' });
      return;
    }
    if (!response.ok) {
      setFailure(await failureOf(response));
      return;
    }
    const body = (await response.json().catch(() => null)) as { data?: unknown } | null;
    const parsed = dealScenarioSchema.array().safeParse(body?.data);
    if (!parsed.success) {
      setFailure({ kind: 'error', code: 'validation_failed' });
      return;
    }
    setFailure(null);
    setScenarios(parsed.data);
  }, [session, dealId]);

  const loading = useFirstLoad(reload);
  useLiveInvalidation(DEAL_TOPICS, reload);
  return { scenarios, loading, failure, reload };
}

/**
 * One scenario, and every write against it. Each write answers with the whole scenario, so
 * `write` swaps the state for the answer — the tiles, the grid and the schedule move together.
 */
export function useScenarioDetail(dealId: string, scenarioId: string) {
  const session = useSession();
  const call = useScenarioCall(dealId);
  const [detail, setDetail] = useState<DealScenarioDetail | null>(null);
  const [failure, setFailure] = useState<ScreenFailure | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    const response = await session
      .request(scenarioPath(dealId, `/${encodeURIComponent(scenarioId)}`))
      .catch(() => null);
    if (response === null) {
      setFailure({ kind: 'error', code: 'unavailable' });
      return;
    }
    if (!response.ok) {
      setFailure(await failureOf(response));
      return;
    }
    const parsed = dealScenarioDetailSchema.safeParse(await response.json().catch(() => null));
    if (!parsed.success) {
      setFailure({ kind: 'error', code: 'validation_failed' });
      return;
    }
    setFailure(null);
    setDetail(parsed.data);
  }, [session, dealId, scenarioId]);

  const loading = useFirstLoad(reload);
  useLiveInvalidation(DEAL_TOPICS, reload);

  /**
   * `rest` is relative to the scenario: `''`, `/lines`, `/cells`. Resolves to the refusal's
   * sentence, or the scenario the server answered with (null for a 204 or an unreadable body,
   * after a reload) — a caller that made a line or a resource finds it in the answer.
   */
  const send = useCallback(
    async (rest: string, method: string, body?: unknown): Promise<SendResult> => {
      const answer = await call(`/${encodeURIComponent(scenarioId)}${rest}`, method, body);
      if (typeof answer === 'string') return answer;
      if (answer.status === 204) {
        await reload();
        return null;
      }
      const parsed = dealScenarioDetailSchema.safeParse(await answer.json().catch(() => null));
      if (parsed.success) {
        setDetail(parsed.data);
        return parsed.data;
      }
      await reload();
      return null;
    },
    [call, scenarioId, reload],
  );

  /** The same, for a caller that only needs to know whether it was refused. */
  const write = useCallback(
    async (rest: string, method: string, body?: unknown): Promise<string | null> => {
      const answer = await send(rest, method, body);
      return typeof answer === 'string' ? answer : null;
    },
    [send],
  );

  return { detail, loading, failure, reload, write, send, call };
}

/** A refusal's sentence, or the answered scenario (null when there was nothing to read). */
export type SendResult = string | DealScenarioDetail | null;
export type ScenarioSend = (rest: string, method: string, body?: unknown) => Promise<SendResult>;
export type ScenarioWrite = (
  rest: string,
  method: string,
  body?: unknown,
) => Promise<string | null>;
